import * as React from 'react';
import styled, { css, keyframes, ThemeContext } from 'styled-components';
import { PaletteGroupKeys, ColorSet, createColorSet, seededRandomColorSet } from "--components/theme";
import { Modify } from '../../common/utils';
import Color from 'color';

type OffsetTypes = "circle" | "square";
type OffsetPosition = "top-right" | "bottom-right";

type BaseBadgeProps = {
    squareEdge?: true
    offsetPosition?: OffsetPosition
    offset?: OffsetTypes
    size?: "small" | "default" | "large"
    pulse?: boolean;
    colorSeed?: string;
    noPadding?: boolean;
    square?: boolean;
}

type StyledBadgeProps = BaseBadgeProps & {
    colorSet: ColorSet
    pip?: boolean
}

type BadgeProps = Modify<React.HtmlHTMLAttributes<HTMLDivElement>, {
    value?: React.ReactNode | false;
    max?: number;
    color?: ColorSet | PaletteGroupKeys | Color,
} & BaseBadgeProps>

const BadgePulsingAnimation = keyframes`
    50% { opacity: 0.5 };
`;

const StyledBadge = styled.span<StyledBadgeProps>`    
    ${props => props.theme.typography.body3};
    ${props => getOverlapStyles(props.offset, props.offsetPosition)};

    display: inline-flex;
    align-items: center;
    justify-content: center;
    
    min-height: ${props => props.pip ? "12px" : props.size == "small" ? "16px" : "20px"};
    min-width: ${props => props.pip ? "4px" : props.size == "small" ? props.square ? "16px" : "8px" : props.square ? "20px" : "12px"};
    line-height: 1em;
    margin: 0;
    padding: ${props => !props.noPadding && props.theme.spacing(0, 0.5)};
    border-radius: ${props => props.squareEdge ? props.theme.radius.small : "100px"};
    white-space: nowrap;
    background-color:${props => props.colorSet.main};
    color: ${props => props.colorSet.contrast};

    ${props => props.pulse && css`
        animation: ${BadgePulsingAnimation} 2.5s ease-in-out infinite;
    `}
`;

const getOverlapStyles = (offset?: OffsetTypes, offsetPosition?: OffsetPosition) => {
    const alignBottom = offsetPosition == "bottom-right";
    if (!offset) return null;
    else return css`
        position: absolute;
        ${alignBottom ? "bottom" : "top"}: ${offset == "circle" ? "14%" : 0};
        right: ${offset == "circle" ? "14%" : 0};
        transform: translate(50%, ${alignBottom ? "50%" : "-50%"});
    `;
}

const Wrapper = styled.div`
    display: inline-flex;
    position: relative;
    height: fit-content;
`;

const Badge: React.FC<BadgeProps> = (props) => {
    const { value: valueProp, max, offset, offsetPosition, size, children, color, squareEdge, pulse, colorSeed, noPadding, square, ...containerProps } = props;
    const theme = React.useContext(ThemeContext);

    let pip = false;
    let value = valueProp;
    if (typeof valueProp === "number") {
        if (max && valueProp > max) value = `${max}+`;
        else if (valueProp >= 0) value = valueProp;
        else value = null;
    }
    if (typeof valueProp == "undefined") pip = true;

    let colorSet: ColorSet;
    if (color instanceof Color) colorSet = createColorSet(color.hex(), theme.style)
    else if (colorSeed) colorSet = seededRandomColorSet(colorSeed)
    else if (typeof color === "object") colorSet = color;
    else if (!color) colorSet = theme.palette.primary;
    else colorSet = theme.palette[color];
    return <Wrapper {...containerProps}>
        {children}
        {value !== false && <StyledBadge
            pip={pip}
            offset={children ? offset || "square" : undefined}
            offsetPosition={offsetPosition}
            colorSet={colorSet}
            size={size}
            squareEdge={squareEdge}
            pulse={pulse}
            noPadding={noPadding}
            square={square}>
            {value}
        </StyledBadge>}
    </Wrapper>;
}

export default Badge;
export { Badge, BadgeProps };